import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileText,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useLanguage } from 'shared/context/LanguageContext';
import { cn } from 'shared/lib/utils';
import type { FileMeta } from 'features/onboarding/types/onboarding.types';

export interface FilePreviewItem {
  /** Translated section label shown above the file name. */
  label: string;
  file: FileMeta;
}

interface FilePreviewModalProps {
  open: boolean;
  items: FilePreviewItem[];
  initialIndex?: number;
  onClose: () => void;
}

const isImage = (f: FileMeta): boolean => f.type.startsWith('image/');
const isPdf = (f: FileMeta): boolean => f.type === 'application/pdf';

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  open,
  items,
  initialIndex = 0,
  onClose,
}) => {
  const { t } = useTranslation();
  const { dir } = useLanguage();
  const [index, setIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [loadFailed, setLoadFailed] = useState(false);

  // Reset state whenever the modal opens or the requested index changes so a
  // re-opened preview never inherits zoom from a previous file.
  useEffect(() => {
    if (open) {
      setIndex(Math.min(Math.max(initialIndex, 0), Math.max(items.length - 1, 0)));
      setZoom(1);
      setLoadFailed(false);
    }
  }, [open, initialIndex, items.length]);

  useEffect(() => {
    setZoom(1);
    setLoadFailed(false);
  }, [index]);

  const current = items[index];
  const total = items.length;

  const goPrev = useCallback(() => {
    setIndex((i) => (i - 1 + total) % total);
  }, [total]);
  const goNext = useCallback(() => {
    setIndex((i) => (i + 1) % total);
  }, [total]);

  // Arrow keys are flipped in RTL so "previous" still feels natural.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        if (dir === 'rtl') goNext();
        else goPrev();
      } else if (e.key === 'ArrowRight') {
        if (dir === 'rtl') goPrev();
        else goNext();
      } else if (e.key === '+' || e.key === '=') {
        setZoom((z) => Math.min(z + 0.25, MAX_ZOOM));
      } else if (e.key === '-' || e.key === '_') {
        setZoom((z) => Math.max(z - 0.25, MIN_ZOOM));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, goPrev, goNext, dir]);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const PrevIcon = dir === 'rtl' ? ChevronRight : ChevronLeft;
  const NextIcon = dir === 'rtl' ? ChevronLeft : ChevronRight;

  const body = useMemo(() => {
    if (!current) return null;
    if (loadFailed) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 text-center text-white/90 p-6">
          <p className="text-sm">{t('review.fileViewer.loadError')}</p>
          <a
            href={current.file.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-gray-900 text-sm font-semibold hover:bg-gray-100"
          >
            <ExternalLink size={16} />
            <span>{t('review.fileViewer.openInTab')}</span>
          </a>
        </div>
      );
    }
    if (isImage(current.file)) {
      return (
        <img
          src={current.file.url}
          alt={current.file.name}
          onError={() => setLoadFailed(true)}
          className="max-h-full max-w-full object-contain select-none transition-transform duration-150"
          style={{ transform: `scale(${zoom})` }}
          draggable={false}
        />
      );
    }
    if (isPdf(current.file)) {
      return (
        <iframe
          title={current.file.name}
          src={current.file.url}
          onError={() => setLoadFailed(true)}
          className="w-full h-full bg-white rounded-lg"
        />
      );
    }
    return (
      <div className="flex flex-col items-center justify-center gap-3 text-white/90">
        <FileText size={48} />
        <p className="text-sm">{current.file.name}</p>
        <a
          href={current.file.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-gray-900 text-sm font-semibold hover:bg-gray-100"
        >
          <ExternalLink size={16} />
          <span>{t('review.fileViewer.openInTab')}</span>
        </a>
      </div>
    );
  }, [current, loadFailed, t, zoom]);

  if (typeof document === 'undefined') return null;
  if (!current) return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex flex-col"
          role="dialog"
          aria-modal="true"
          aria-label={current.file.name}
        >
          <button
            type="button"
            aria-label={t('review.fileViewer.close') as string}
            onClick={onClose}
            className="absolute inset-0 bg-black/85"
          />

          <header className="relative z-10 flex items-center gap-3 px-4 sm:px-6 py-3 text-white">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-white/60 truncate">
                {current.label}
              </p>
              <p className="text-sm font-medium truncate">{current.file.name}</p>
            </div>

            {total > 1 && (
              <span className="text-xs font-medium text-white/70 tabular-nums">
                {index + 1} / {total}
              </span>
            )}

            {isImage(current.file) && !loadFailed && (
              <div className="hidden sm:flex items-center gap-1">
                <button
                  type="button"
                  aria-label="zoom out"
                  onClick={() => setZoom((z) => Math.max(z - 0.25, MIN_ZOOM))}
                  disabled={zoom <= MIN_ZOOM}
                  className="w-9 h-9 rounded-lg flex items-center justify-center bg-white/10 hover:bg-white/20 disabled:opacity-40 transition-colors"
                >
                  <ZoomOut size={16} />
                </button>
                <button
                  type="button"
                  aria-label="zoom in"
                  onClick={() => setZoom((z) => Math.min(z + 0.25, MAX_ZOOM))}
                  disabled={zoom >= MAX_ZOOM}
                  className="w-9 h-9 rounded-lg flex items-center justify-center bg-white/10 hover:bg-white/20 disabled:opacity-40 transition-colors"
                >
                  <ZoomIn size={16} />
                </button>
              </div>
            )}

            <a
              href={current.file.url}
              target="_blank"
              rel="noreferrer"
              className="w-9 h-9 rounded-lg flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors"
              title={t('review.fileViewer.openInTab') as string}
              aria-label={t('review.fileViewer.openInTab') as string}
            >
              <ExternalLink size={16} />
            </a>
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-lg flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors"
              aria-label={t('review.fileViewer.close') as string}
            >
              <X size={18} />
            </button>
          </header>

          <div className="relative z-10 flex-1 flex items-center justify-center px-4 sm:px-12 pb-6 min-h-0">
            {total > 1 && (
              <button
                type="button"
                onClick={goPrev}
                aria-label={t('review.fileViewer.previous') as string}
                className={cn(
                  'absolute top-1/2 -translate-y-1/2 z-10',
                  'w-11 h-11 rounded-full flex items-center justify-center bg-white/15 hover:bg-white/25 text-white transition-colors',
                  dir === 'rtl' ? 'right-2 sm:right-4' : 'left-2 sm:left-4',
                )}
              >
                <PrevIcon size={20} />
              </button>
            )}

            <div className="w-full h-full max-w-5xl max-h-[80vh] flex items-center justify-center overflow-hidden">
              {body}
            </div>

            {total > 1 && (
              <button
                type="button"
                onClick={goNext}
                aria-label={t('review.fileViewer.next') as string}
                className={cn(
                  'absolute top-1/2 -translate-y-1/2 z-10',
                  'w-11 h-11 rounded-full flex items-center justify-center bg-white/15 hover:bg-white/25 text-white transition-colors',
                  dir === 'rtl' ? 'left-2 sm:left-4' : 'right-2 sm:right-4',
                )}
              >
                <NextIcon size={20} />
              </button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default FilePreviewModal;
